import React, { useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EnvelopeIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { useAppDispatch } from '../../hooks/redux';
import { logout } from '../../store/slices/authSlice';
import { localApi } from '../../store/api/localApi';

/**
 * /parent/onboarding — empty state for parents with zero linked children.
 *
 * Should be rare: a verified parent normally has at least one child by the
 * time they reach the app. This shows up when:
 *   - A parent invitation was sent but the link to a specific child failed
 *     (data quality bug — worth flagging).
 *   - A staff member moves all of a parent's kids elsewhere and removes the
 *     last membership while the parent is logged in.
 *   - Local-dev edge cases.
 *
 * Copy avoids blame and points at the most likely path forward: contact the
 * inviting location. Sign-out is offered for the case where the parent
 * landed on the wrong account entirely.
 */
const ParentOnboarding: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const handleSignOut = useCallback(() => {
    dispatch(logout());
    dispatch(localApi.util.resetApiState());
    navigate('/login');
  }, [dispatch, navigate]);

  return (
    <div className="py-10">
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow p-6 text-center space-y-4">
          <div className="mx-auto h-12 w-12 rounded-full bg-primary-50 text-primary-700 flex items-center justify-center">
            <UserPlusIcon className="h-6 w-6" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">No children linked yet</h1>
          <p className="text-sm text-gray-600">
            Your account doesn't have any children attached. This usually means the location hasn't
            finished setting up your child's profile, or the invite link was sent to a different
            email address.
          </p>
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-left text-sm text-amber-900">
            <div className="flex gap-2">
              <EnvelopeIcon
                aria-hidden="true"
                className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5"
              />
              <div>
                <p className="font-medium">What to do</p>
                <p className="mt-1">
                  Contact the location that invited you. They can confirm the email on file and
                  re-send the invitation.
                </p>
              </div>
            </div>
          </div>
          <div className="pt-2 flex flex-col sm:flex-row gap-2 sm:justify-center">
            <Link
              to="/profile"
              data-testid="parent-onboarding-profile"
              className="inline-flex justify-center px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              View my profile
            </Link>
            <button
              type="button"
              data-testid="parent-onboarding-signout"
              onClick={handleSignOut}
              className="inline-flex justify-center px-4 py-2 rounded-md border border-transparent text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentOnboarding;
